import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './LevelUpCelebration.css';

interface LevelUpContextType {
  triggerLevelUp: (newLevel: number) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const LevelUpContext = createContext<LevelUpContextType>({ triggerLevelUp: () => {} });

export const useLevelUp = () => useContext(LevelUpContext);

const CONFETTI_COLORS = [
  '#2D6A4F', '#52B788', '#95D5B2', '#FBBF24',
  '#F97316', '#EC4899', '#8B5CF6', '#3B82F6',
];

export const LevelUpCelebration: React.FC<{ level: number; onDismiss: () => void }> = ({
  level,
  onDismiss,
}) => {
  const { t } = useTranslation('common');

  useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="levelup-overlay" onClick={onDismiss} role="button" tabIndex={0}>
      {Array.from({ length: 24 }, (_, i) => (
        <div
          key={i}
          className="levelup-confetti-piece"
          style={{
            '--x': `${Math.random() * 100}vw`,
            '--delay': `${Math.random() * 0.5}s`,
            '--color': CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            '--rotation': `${Math.random() * 360}deg`,
            '--size': `${6 + Math.random() * 6}px`,
          } as React.CSSProperties}
        />
      ))}
      <div className="levelup-center">
        <div className="levelup-label">
          {t('levelUp', 'LEVEL UP!')}
        </div>
        <div className="levelup-number">{level}</div>
      </div>
    </div>
  );
};

export const LevelUpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeLevel, setActiveLevel] = useState<number | null>(null);

  const triggerLevelUp = useCallback((newLevel: number) => {
    setActiveLevel(newLevel);
  }, []);

  const handleDismiss = useCallback(() => {
    setActiveLevel(null);
  }, []);

  return (
    <LevelUpContext.Provider value={{ triggerLevelUp }}>
      {children}
      {activeLevel !== null && (
        <LevelUpCelebration level={activeLevel} onDismiss={handleDismiss} />
      )}
    </LevelUpContext.Provider>
  );
};
